// Zusatzangaben des Bereichs „Bearbeitung“, für die der Schaden (noch) keine
// eigene Spalte hat: Nummer beim Kunden, Schadenursache, Bild, frei hinzugefügte
// Beteiligte und welcher der drei Status gerade „am Zug“ ist. Sie liegen im
// jsonb-Feld `Damage.meldung` unter dem Schlüssel `mappe`; der Server legt einen
// Meldungs-Patch auf oberster Ebene über den Bestand, `mappe` wird deshalb
// immer vollständig geschickt.
import type { BigAnswers } from "../../schadenmeldung-big/types";

export type AmZug = "vn" | "vr" | "rs";

export interface FreierBeteiligter {
  rolle: string;
  name: string;
  contactId?: number | null;
  tel?: string;
  email?: string;
}

export interface MappeZusatz {
  nrKunde: string;
  ursache: string;
  /** Verkleinertes Bild als Data-URL (JPEG, höchstens ~60 KB). */
  bild: string | null;
  amZug: AmZug | null;
  beteiligte: FreierBeteiligter[];
}

export const MAPPE_LEER: MappeZusatz = { nrKunde: "", ursache: "", bild: null, amZug: null, beteiligte: [] };

const str = (x: unknown): string => (typeof x === "string" ? x : "");

export function mappeAusMeldung(meldung: Record<string, unknown> | null | undefined): MappeZusatz {
  const roh = meldung?.mappe;
  if (!roh || typeof roh !== "object" || Array.isArray(roh)) return MAPPE_LEER;
  const m = roh as Record<string, unknown>;
  const amZug = m.amZug === "vn" || m.amZug === "vr" || m.amZug === "rs" ? m.amZug : null;
  const beteiligte: FreierBeteiligter[] = (Array.isArray(m.beteiligte) ? m.beteiligte : [])
    .filter((b): b is Record<string, unknown> => !!b && typeof b === "object")
    .map((b) => ({
      rolle: str(b.rolle),
      name: str(b.name),
      contactId: typeof b.contactId === "number" ? b.contactId : null,
      tel: str(b.tel),
      email: str(b.email),
    }))
    .filter((b) => b.name.trim() !== "");
  return {
    nrKunde: str(m.nrKunde),
    ursache: str(m.ursache),
    bild: typeof m.bild === "string" && m.bild.startsWith("data:image/") ? m.bild : null,
    amZug,
    beteiligte,
  };
}

/** Rollen am Schaden für „Beteiligten hinzufügen“ (frei ergänzbar). */
export const ROLLEN = [
  "Anspruchsteller",
  "Geschädigter",
  "Verursacher",
  "Zeuge",
  "Fahrer",
  "Halter",
  "Mieter",
  "Eigentümer",
  "Werkstatt",
  "Handwerker",
  "Gutachter",
  "Rechtsanwalt",
  "Polizei",
  "Ansprechpartner",
] as const;

/** Das Feld der Meldung, aus dem der Server die Schadenhöhe ableitet (je Schadenart). */
export function hoeheFeld(a: BigAnswers): "gebSchadenhoehe" | "kfzEigenHoehe" | "sonSacheHoehe" | "sonSonstHoehe" | null {
  const r = a as unknown as Record<string, unknown>;
  if (a.damageType === "gebaeude") return "gebSchadenhoehe";
  if (a.damageType === "kfz") return "kfzEigenHoehe";
  if (a.damageType === "sonstiges") return str(r.sonSonstHoehe) && !str(r.sonSacheHoehe) ? "sonSonstHoehe" : "sonSacheHoehe";
  return null;
}

/** „1.250,50“ / „1250.5“ / „ca. 300 €“ → Zahl; leer oder unlesbar → null (wie `betragAusText` im Backend). */
export function betragAusText(wert: string): number | null {
  const treffer = wert.replace(/\s/g, "").match(/-?\d{1,3}(?:\.\d{3})+(?:,\d+)?|-?\d+(?:,\d+)?/);
  if (!treffer) return null;
  const zahl = Number(treffer[0].replace(/\./g, "").replace(",", "."));
  return Number.isFinite(zahl) ? zahl : null;
}

export function betragText(n: number | string | null | undefined): string {
  if (n === null || n === undefined || n === "") return "";
  const zahl = Number(n);
  if (!Number.isFinite(zahl)) return "";
  return zahl.toLocaleString("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

/** ISO-/Datumstext → `YYYY-MM-DD` für `<input type="date">`. */
export function tagAus(wert: string | null | undefined): string {
  if (!wert) return "";
  const m = /^(\d{4}-\d{2}-\d{2})/.exec(wert);
  return m ? m[1] : "";
}

export function datumText(wert: string | null | undefined): string {
  const tag = tagAus(wert);
  if (!tag) return "";
  const [j, m, t] = tag.split("-");
  return `${t}.${m}.${j}`;
}

/** Bild auf höchstens `kante` Pixel verkleinern und als JPEG-Data-URL liefern. */
export async function bildVerkleinern(datei: File, kante = 420): Promise<string> {
  const url = URL.createObjectURL(datei);
  try {
    const img = await new Promise<HTMLImageElement>((ok, fehler) => {
      const el = new Image();
      el.onload = () => ok(el);
      el.onerror = () => fehler(new Error("Das Bild konnte nicht gelesen werden."));
      el.src = url;
    });
    const faktor = Math.min(1, kante / Math.max(img.width, img.height));
    const canvas = document.createElement("canvas");
    canvas.width = Math.max(1, Math.round(img.width * faktor));
    canvas.height = Math.max(1, Math.round(img.height * faktor));
    const ctx = canvas.getContext("2d");
    if (!ctx) throw new Error("Das Bild konnte nicht verarbeitet werden.");
    ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
    return canvas.toDataURL("image/jpeg", 0.8);
  } finally {
    URL.revokeObjectURL(url);
  }
}
